import { get, set, del, clear, update } from 'idb-keyval'

export interface IDBEntityCacheOptions {
   ttlMs?: number         // Time to live in ms
   version?: string       // Optional version string for cache invalidation
}

const DEFAULT_TTL_MS = 1000 * 60 * 10 // 10 minutes

function getKey(entity: string) {
   return `${entity}Cache`
}
function getTimestampKey(entity: string) {
   return `${entity}CacheTimestamp`
}
function getVersionKey(entity: string) {
   return `${entity}CacheVersion`
}

export async function getCachedEntity<T = any[]>(
   entity: string,
   options: IDBEntityCacheOptions = {}
): Promise<T | null> {
   try {
      const [data, timestamp, version] = await Promise.all([
         get(getKey(entity)),
         get(getTimestampKey(entity)),
         get(getVersionKey(entity)),
      ])
      const now = Date.now()
      const ttl = options.ttlMs ?? DEFAULT_TTL_MS
      if (
         data &&
         timestamp &&
         now - timestamp < ttl &&
         (!options.version || version === options.version)
      ) {
         return data as T
      }
      return null
   } catch {
      return null
   }
}

export async function setCachedEntity<T = any[]>(
   entity: string,
   data: T,
   options: IDBEntityCacheOptions = {}
): Promise<void> {
   try {
      await Promise.all([
         set(getKey(entity), data),
         set(getTimestampKey(entity), Date.now()),
         options.version ? set(getVersionKey(entity), options.version) : Promise.resolve(),
      ])
   } catch {
      // Ignore cache write errors
   }
}

export async function updateCachedEntity<T = any[]>(
   entity: string,
   data: T,
) {
   try {
      await update(getKey(entity), () => data)
   } catch {
      // Ignore cache write errors
   }
}

export async function clearCachedIDBByEntityKey(entity: string): Promise<void> {
   await Promise.all([
      del(getKey(entity)),
      del(getTimestampKey(entity)),
      del(getVersionKey(entity)),
   ])
}

export async function clearAllCachedIDBEntities(): Promise<void> {
   await clear()
}
